import { v4 } from 'uuid'
import { logger } from 'v2/utils/logger'
import type { FlowNodeTypes, GenericEdge, GenericNode } from '../types'
import { createAndAddIntentToAiNode, isAiAgentOrActionNode } from './ai-agent-utils'
import { getDefaultNodeData, getNodeVarsForHandle } from './index'

interface ConnectChildNodeArgs {
  sourceNode: GenericNode
  // Logical output handle the new node hangs off (e.g. an output-state id, a
  // trigger handle, or 'answered'). For AI sources this is NOT the edge handle —
  // see below — but it IS what edge nodeVars are derived from.
  outputHandle: string
  nodeType: FlowNodeTypes
  position: { x: number; y: number }
  getNodes: () => GenericNode[]
  addNodes: (nodes: GenericNode[]) => void
  addEdges: (edges: GenericEdge[]) => void
  updateNodeInternals: (id: string) => void
}

// Creates `nodeType` as a child of `sourceNode` and wires the connecting edge.
// AI sources (ai_agent_v2 / ai_action) connect through a freshly created intent
// handle, while the edge's nodeVars are always derived from the source's logical
// output handle (never the synthetic intent handle). Returns the new node, or
// null if an AI-source intent could not be created.
export const connectChildNode = ({
  sourceNode,
  outputHandle,
  nodeType,
  position,
  getNodes,
  addNodes,
  addEdges,
  updateNodeInternals,
}: ConnectChildNodeArgs): GenericNode | null => {
  let sourceHandle = outputHandle
  if (isAiAgentOrActionNode(sourceNode.type)) {
    const intentId = createAndAddIntentToAiNode(sourceNode.id)
    if (!intentId) {
      logger.error('Failed to create intent for AI node')
      return null
    }
    sourceHandle = intentId
  }
  updateNodeInternals(sourceNode.id)

  const newNode: GenericNode = {
    id: v4(),
    type: nodeType,
    position,
    data: getDefaultNodeData(nodeType, getNodes),
    zIndex: 1000,
  }
  addNodes([newNode])
  addEdges([
    {
      id: v4(),
      source: sourceNode.id,
      sourceHandle,
      target: newNode.id,
      type: 'button',
      data: { nodeVars: getNodeVarsForHandle(sourceNode, outputHandle) },
    },
  ])

  return newNode
}
